import {
  Box,
  Button,
  Container,
  Heading,
  Icon,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import { FaAndroid, FaWindows } from 'react-icons/fa';

const MAIN_APP_FALLBACK_URL = 'https://shop-n-smile.github.io';

const trimTrailingSlash = (url: string) => url.replace(/\/+$/, '');

const resolveStage = () => {
  const stage = (
    import.meta.env.VITE_STAGE as string | undefined
  )?.toLowerCase();
  if (stage) {
    return stage;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host.startsWith('pos.')) {
      const hostParts = host.split('.');
      if (hostParts.length >= 4) {
        return hostParts[1];
      }

      if (hostParts.length === 3) {
        return 'prod';
      }
    }
  }

  return undefined;
};

const resolveMainAppBaseUrl = () => {
  const configuredUrl = (
    import.meta.env.VITE_MAIN_APP_URL as string | undefined
  )?.trim();
  if (configuredUrl) {
    return trimTrailingSlash(configuredUrl);
  }

  const stage = resolveStage();

  if (stage === 'prod' || stage === 'production') {
    return 'https://shopnsmile.org';
  }

  if (stage && stage !== 'local') {
    return `https://main.${stage}.shopnsmile.org`;
  }

  return MAIN_APP_FALLBACK_URL;
};

const Hero = () => {
  const bgGradient = useColorModeValue(
    'linear(to-br, brand.50, white)',
    'linear(to-br, gray.900, gray.800)',
  );

  const androidBg = useColorModeValue('green.500', 'green.400');
  const androidHoverBg = useColorModeValue('green.600', 'green.500');
  const windowsBg = useColorModeValue('blue.500', 'blue.400');
  const windowsHoverBg = useColorModeValue('blue.600', 'blue.500');
  const mainAppBaseUrl = resolveMainAppBaseUrl();
  const androidPlatformUrl = `${mainAppBaseUrl}/releases/platforms/android`;
  const windowsPlatformUrl = `${mainAppBaseUrl}/releases/platforms/windows`;

  return (
    <Box
      as="section"
      bg={bgGradient}
      pt={{ base: 20, md: 28 }}
      pb={{ base: 16, md: 24 }}>
      <Container maxW="container.xl">
        <Stack spacing={8} maxW="3xl" mx="auto" textAlign="center">
          <Heading
            as="h1"
            fontSize={{ base: '3xl', sm: '4xl', md: '5xl', lg: '6xl' }}
            fontWeight="bold"
            lineHeight="shorter"
            color={useColorModeValue('gray.900', 'white')}>
            Shop & Smile™
            <Text as="span" color="brand.500" display="block">
              Retail Software Built for Growth
            </Text>
          </Heading>

          <Stack
            direction={{ base: 'column', sm: 'row' }}
            spacing={4}
            justify="center"
            pt={4}>
            <Button
              size="lg"
              leftIcon={<Icon as={FaAndroid} boxSize={6} />}
              bg={androidBg}
              color="white"
              _hover={{
                bg: androidHoverBg,
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
              }}
              transition="all 0.2s"
              borderRadius="full"
              px={8}
              py={6}
              fontSize={{ base: 'md', md: 'lg' }}
              width={{ base: 'full', sm: 'auto' }}
              as="a"
              href={androidPlatformUrl}
              rel="noreferrer"
              target="_blank">
              Download for Android
            </Button>

            <Button
              size="lg"
              as="a"
              leftIcon={<Icon as={FaWindows} boxSize={5} />}
              bg={windowsBg}
              color="white"
              _hover={{
                bg: windowsHoverBg,
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
              }}
              transition="all 0.2s"
              borderRadius="full"
              px={8}
              py={6}
              fontSize={{ base: 'md', md: 'lg' }}
              width={{ base: 'full', sm: 'auto' }}
              href={windowsPlatformUrl}
              rel="noreferrer"
              target="_blank">
              Download for Windows
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Hero;
